import { CONCEPTS, ConceptId, formatDimension } from './concepts';
import { OPERATORS, OperatorId } from './operators';

export interface CastInput {
  conceptId: ConceptId;
  secondConceptId?: ConceptId;
  operatorId: OperatorId;
  modifierIds: string[];
}

export type CostTier = 'trivial' | 'modest' | 'significant' | 'catastrophic';

export interface CastResult {
  dimensionSummary: string;
  costLabel: string;
  costTier: CostTier;
  effect: string[];
  warnings: string[];
}

const C_SQUARED = 8.987551787e16; // (speed of light)^2, m^2/s^2 -> J/kg for E=mc^2
const TNT_JOULES_PER_TON = 4.184e9;

function has(mods: string[], id: string): boolean {
  return mods.includes(id);
}

function magnitudeScale(mods: string[]): { label: string; scale: number } {
  if (has(mods, 'unbound')) return { label: 'Unbound', scale: 100 };
  if (has(mods, 'precise')) return { label: 'Precise', scale: 1 };
  // No magnitude modifier selected -- Medium was removed as a selectable rune
  // (2026-07-28, provably a no-op even when chosen), but the ×10 default tier
  // itself still exists and still needs a label for the abstract cost text.
  // "Standard" reads as "what happens with nothing specified," not a third
  // peer option sitting alongside Precise/Unbound.
  return { label: 'Standard', scale: 10 };
}

function tierFromCost(cost: number): CostTier {
  if (cost < 50) return 'trivial';
  if (cost < 500) return 'modest';
  if (cost < 50000) return 'significant';
  return 'catastrophic';
}

function formatCost(cost: number, unit: string): string {
  if (cost >= 1000) return `${cost.toExponential(2)} ${unit}`;
  return `${Math.round(cost * 100) / 100} ${unit}`;
}

// Not every operator/concept combination has to mean something — per design/
// 01_Vision.md and 03_Rune_Language.md, most combinations should do nothing, and some
// should fail dangerously. This is the actual fallback mechanic for combinations with
// no coherent physical pathway, rather than open-ended "needs more thought" hedging:
// Precise magnitude is safe to probe (nothing happens); pushing past that risks a
// catastrophic backfire, because conservation of energy still applies even when the
// simulation can't find a valid pathway — the energy has to come from somewhere, and
// absent a real pathway it defaults to draining the caster.
function unresolvedOutcome(scale: number): { effect: string[]; warnings: string[]; cost: number } {
  if (scale === 1) {
    return {
      effect: [
        'Nothing happens — no coherent physical pathway exists for this combination, and too little was pushed through to fail dangerously either.',
      ],
      warnings: [],
      cost: 0.5,
    };
  }
  return {
    effect: [
      "The simulation finds no coherent pathway to satisfy this expression — but conservation of energy still applies, so it defaults to draining the caster's own reserves to force an outcome.",
    ],
    warnings: [
      'Potentially lethal. Undefined combinations are safe to probe at Precise magnitude — pushing past that risks a catastrophic backfire.',
    ],
    cost: scale * 5000,
  };
}

export function resolve(input: CastInput): CastResult {
  const concept = CONCEPTS[input.conceptId];
  const operator = OPERATORS[input.operatorId];
  const { label: magLabel, scale } = magnitudeScale(input.modifierIds);
  const effect: string[] = [`Magnitude: ${magLabel} (×${scale}).`];
  const warnings: string[] = [];
  let cost = scale * concept.baseCost;
  let unit = 'cost units (abstracted)';

  switch (operator.id) {
    case 'transfer': {
      const targetToSelf = has(input.modifierIds, 'reverse');
      const from = targetToSelf ? 'target' : 'caster';
      const to = targetToSelf ? 'caster' : 'target';

      if (concept.isEntropy) {
        // Reverse now does double duty: direction and sign used to be
        // independent modifiers (four reachable combinations), but both read
        // the same one post-merge, so only the two "aligned" pairings remain
        // -- see 07_Open_Questions.md.
        const decreasing = targetToSelf;
        if (decreasing) {
          cost *= 10;
          effect.push(
            `Pulls ${to} toward lower local Entropy (more order) — reads as repair, purification, or healing.`,
          );
          effect.push(
            `${from} pays for it directly: its own Entropy rises by at least as much as ${to}'s falls — like a refrigerator dumping more heat into the room than it removes from the inside.`,
          );
          warnings.push(
            `Decreasing Entropy is the expensive direction: the Second Law only allows it if ${from} overshoots in the other direction, which is what the Energy cost actually pays for.`,
          );
        } else {
          cost *= 0.1;
          effect.push(
            `Nudges ${to} toward its natural higher-Entropy state — accelerated decay, corrosion, or disorder.`,
          );
          effect.push(`${from} correspondingly cools/orders — the same pairing as heat flowing from a hot source to a cooler target.`);
          effect.push('Cheap: this goes with the Second Law, not against it.');
        }
      } else if (concept.id === 'energy') {
        effect.push(`Moves stored heat/Energy from ${from} to ${to}.`);
        effect.push(targetToSelf ? `${to} warms sharply; ${from} cools.` : `${to} warms; ${from} cools/depletes.`);
      } else if (concept.id === 'momentum') {
        if (has(input.modifierIds, 'oscillating')) {
          cost *= 1.5;
          effect.push(`Imparts Momentum to ${to} back and forth periodically rather than one direction — mechanical vibration/shaking.`);
          warnings.push(`${from} feels the reaction vibration too — conservation of momentum applies each half-cycle, not just once.`);
        } else {
          effect.push(`Moves Momentum from ${from} to ${to} — a push/throw.`);
          warnings.push(`${from} recoils — conservation of momentum means this is never free for the source.`);
        }
      } else if (concept.id === 'charge') {
        if (has(input.modifierIds, 'oscillating')) {
          cost *= 1.5;
          effect.push(
            `Drives Charge back and forth between ${from} and ${to} rather than one direction — alternating current.`,
          );
          effect.push(
            'Net Charge transferred per cycle is ~zero, but real work is still done each half-cycle — this is why AC power delivers energy without net-relocating charge.',
          );
        } else {
          effect.push(`Moves Charge from ${from} to ${to} — electrifies the receiving locus.`);
          effect.push(`${from} is left oppositely/less charged.`);
        }
      } else if (concept.id === 'mass') {
        effect.push(`Moves Mass from ${from} to ${to}.`);
        warnings.push('The displaced mass does not vanish — it has to go somewhere (conservation of mass).');
      } else if (concept.id === 'space') {
        effect.push(`Swaps position between ${from} and ${to} — a teleport-style effect.`);
        warnings.push(
          'Per conservation of momentum, this cannot be a free repositioning — expect a hidden momentum cost (a violent reaction) at both ends.',
        );
      } else if (concept.id === 'time') {
        effect.push(`Alters ${to}'s local rate of time passage relative to its surroundings — haste/slow-motion.`);
        warnings.push(
          'Real time dilation requires relativistic velocity or gravitational field strength — this should be an endgame-tier effect, not an early one.',
        );
      }
      break;
    }

    case 'convert': {
      const second = input.secondConceptId ? CONCEPTS[input.secondConceptId] : undefined;
      if (!second) {
        warnings.push('Convert requires a second concept to convert into.');
        break;
      }
      const isMassEnergyPair =
        (concept.id === 'mass' && second.id === 'energy') || (concept.id === 'energy' && second.id === 'mass');

      if (isMassEnergyPair) {
        if (concept.id === 'mass') {
          // Mass -> Energy: magnitude is an input mass in kg.
          const massKg = scale === 1 ? 1e-6 : scale === 10 ? 1e-3 : 1;
          const joules = massKg * C_SQUARED;
          const tonsTnt = joules / TNT_JOULES_PER_TON;
          cost = joules;
          unit = 'J';
          effect.push(`E = mc² — converting ${massKg} kg of Mass releases ${joules.toExponential(3)} J.`);
          effect.push(`That's roughly ${tonsTnt.toExponential(2)} tons of TNT equivalent.`);
        } else {
          // Energy -> Mass: magnitude is an input energy in Joules, not a mass.
          const inputJoules = scale === 1 ? 1e3 : scale === 10 ? 1e6 : 1e9;
          const massKg = inputJoules / C_SQUARED;
          cost = inputJoules;
          unit = 'J';
          effect.push(
            `E = mc² — converting ${inputJoules.toExponential(0)} J of Energy creates ${massKg.toExponential(3)} kg of Mass.`,
          );
          effect.push(`For scale: that's roughly ${(massKg * 1e9).toExponential(2)} micrograms.`);
        }
        warnings.push(
          'This is not a tunable balance number — it is the literal physical conversion factor. Treat as forbidden/late-game/civilization-ending knowledge.',
        );
      } else if (
        (concept.id === 'momentum' && second.id === 'entropy') ||
        (concept.id === 'entropy' && second.id === 'momentum')
      ) {
        cost *= 0.5;
        effect.push('Ordered Momentum becomes disordered heat at the point of contact — braking/friction.');
        effect.push('Cheap-ish: converting order into disorder is the direction the Second Law already favors.');
      } else if (
        (concept.id === 'energy' && second.id === 'momentum') ||
        (concept.id === 'momentum' && second.id === 'energy')
      ) {
        if (has(input.modifierIds, 'oscillating')) {
          cost *= 0.8;
          effect.push(
            'Energy and Momentum trade back and forth periodically instead of converting one-way — simple harmonic motion, like a pendulum, spring, or resonating string.',
          );
          effect.push('Cheaper than a one-way conversion: sustained resonant exchange is recycling energy, not spending it.');
          warnings.push(
            'Only true at resonance — any damping or off-resonance drive bleeds a fraction of each cycle into waste heat/Entropy, same as real oscillators.',
          );
        } else {
          cost *= 1.2;
          if (concept.id === 'energy') {
            effect.push('Stored Energy becomes directed Momentum — propulsion/launch.');
          } else {
            effect.push('Momentum becomes heat as the object decelerates — braking, not cleanly recoverable Energy.');
            warnings.push(
              'Real deceleration usually splits into some recovered Energy and a lot of waste heat — treat this as an optimistic simplification, not a clean 1:1 conversion.',
            );
          }
          warnings.push('No real conversion is 100% efficient — expect a small fraction lost as waste heat/Entropy.');
        }
      } else {
        const compositional = [
          ['mass', 'momentum'],
          ['mass', 'space'],
          ['momentum', 'space'],
        ].some(([a, b]) => (concept.id === a && second.id === b) || (concept.id === b && second.id === a));

        if (compositional) {
          cost = scale * 0.5;
          effect.push(
            `Nothing happens — ${second.name} is already a factor inside ${concept.name}'s own definition (or vice versa). This is a compositional relationship, not something Convert can act on.`,
          );
        } else {
          const outcome = unresolvedOutcome(scale);
          cost = outcome.cost;
          effect.push(...outcome.effect);
          warnings.push(...outcome.warnings);
        }
      }
      break;
    }

    case 'bind':
    case 'separate': {
      const isBind = operator.id === 'bind';

      if (concept.id === 'mass') {
        const reversed = has(input.modifierIds, 'reverse');
        effect.push(
          isBind
            ? 'Fuses/adheres two masses into one connected structure — closer to welding or gluing ("Join") than a passive coupling.'
            : reversed
              ? 'Rejoins two previously-split masses back into one along the same seam they were parted at — the exact inverse of an ordinary Separate·Mass cast, not a second, unrelated operation.'
              : 'Splits one mass into two along a chosen boundary, minimizing released binding energy.',
        );
        effect.push('A structural relationship, not a quantity transfer.');
      } else if (concept.id === 'charge') {
        effect.push(
          isBind
            ? "Strings a conductive link between the target and the source (the marked Source at bind time, defaulting to you) — a wire, with no wire. From here on Charge flows from whichever side holds more toward whichever holds less until the two read the same, however far apart they are and whatever stands between them."
            : 'Cuts an existing Bind·Charge link — each side keeps whatever Charge it is holding at that moment, and stops conducting from here on.',
        );
        effect.push(
          isBind
            ? 'Real current, so real resistive loss: with nothing but the binding itself acting as the conductor, whatever Charge crosses dissipates as heat in the caster. Billed per unit moved — once the two sides read the same, nothing flows and nothing is owed.'
            : 'A structural release, not a quantity transfer — nothing moves at the moment of casting.',
        );
        if (isBind) {
          warnings.push(
            'The older reading — recombining separated positive and negative charge back to neutral, i.e. discharging — is not what this does. It needs a negative-charge model the simulation does not have. See the reference notes.',
          );
        }
      } else if (concept.id === 'energy') {
        effect.push(
          isBind
            ? "Opens a thermal-contact link to the target — from here on, its Energy and the source's own (the marked Source at bind time, defaulting to you) drift toward each other over time, the same Newton's-law-of-cooling shape ambient decay already uses, just between two loci instead of one locus and a room."
            : 'Releases an existing Bind·Energy link — each side keeps whatever Energy it currently holds, independent again from here on.',
        );
        effect.push(
          isBind
            ? "Unlike every other Bind reading, this genuinely moves a real quantity, conserved between the two loci at every step — just spread across many small nudges over time rather than one cast moving a fixed amount."
            : 'A structural release, not a quantity transfer — nothing moves at the moment of casting.',
        );
      } else if (concept.id === 'entropy') {
        effect.push(
          isBind
            ? "Crosses the target over with the source (the marked Source at bind time): from here on, a Transfer of Entropy naming either one is served by the other, however far apart they are. Aim a deposit at one and it lands on the other; draw from one and it comes out of the other."
            : 'Severs an existing Bind·Entropy link — both sides keep whatever Entropy they hold at that moment, and casts stop being displaced from here on.',
        );
        effect.push(
          isBind
            ? "A redirection, not a flow: nothing moves at the moment of casting and nothing crosses the caster afterward, so unlike Bind·Charge and Bind·Energy this one is free to strike and free to hold. Symmetric — neither end is the handle — which means an object's own Entropy becomes reachable only by naming its partner."
            : 'A structural release, not a quantity transfer — nothing moves at the moment of casting.',
        );
        if (isBind) {
          warnings.push(
            'Binding to yourself does nothing: the caster is not an object and has no Entropy reserve for the far end to stand in for. Neither does binding anything that takes no part in Entropy at all.',
          );
          if (has(input.modifierIds, 'reverse')) {
            warnings.push(
              'Reverse has no effect here. It inverts an operator\'s default behavior, and a displacement has no direction to invert: both roles already displace, so the link reads the same from either end. It selected a second mechanic for one day (2026-08-16, an anti-correlated see-saw), which was removed the same day for breaking conservation.',
            );
          }
        }
      } else if (concept.id === 'space' && isBind) {
        effect.push(
          'Fixes the relative position/distance between two loci — a tether or anchor. Moving one drags the other along or resists the motion.',
        );
        effect.push('A structural relationship, not a quantity transfer.');
      } else if (concept.id === 'time' && isBind) {
        effect.push('Best guess: synchronizes the rate of time flow between two loci, resisting relativistic drift. Genuinely speculative.');
        effect.push('A structural relationship, not a quantity transfer.');
      } else if (concept.id === 'momentum') {
        effect.push(
          isBind
            ? "Links the target's own position to yours (or, with Reverse, mirrors it — the target moves the opposite way you just did) — every real step you take, it takes too, until released."
            : 'Releases a Bind·Momentum link, leaving the target to move under its own power (or not at all) from here on.',
        );
        effect.push('A structural relationship, not a quantity transfer.');
      } else {
        // Separate·Space and Separate·Time have no reasoned hypothesis yet
        // (unlike Bind·Space/Bind·Time/either·Momentum above) — genuinely
        // undefined territory, not just "not written yet." Entropy used to sit
        // here too; it moved to a real reading on 2026-08-08 (see above).
        const outcome = unresolvedOutcome(scale);
        cost = outcome.cost;
        effect.push(...outcome.effect);
        warnings.push(...outcome.warnings);
      }
      break;
    }

    case 'measure': {
      cost *= 0.05;
      const oscillating = has(input.modifierIds, 'oscillating');
      if (oscillating) {
        cost *= scale;
        effect.push(
          `Samples repeatedly rather than once — roughly ${scale}× over the casting window, not a single reading.`,
        );
      }
      if (concept.id === 'energy') {
        effect.push("Reads the target's stored Energy/temperature — the real-world equivalent is thermometry.");
      } else if (concept.id === 'mass') {
        effect.push(
          "Reads the target's Mass at range — the real-world equivalent is gravimetric surveying (detecting buried mass via its gravitational pull), which is why this can reveal hidden objects through a wall.",
        );
      } else if (concept.id === 'charge') {
        effect.push('Reads Charge distribution nearby — the real-world equivalent is an electroscope or metal detector.');
      } else if (concept.id === 'momentum') {
        effect.push(
          "Reads an object's Momentum (mass × velocity) — the real-world equivalent is Doppler radar, detecting a moving object's velocity at range.",
        );
      } else if (concept.id === 'space') {
        effect.push('Reads distance/extent — the real-world equivalent is a rangefinder or echolocation.');
      } else if (concept.id === 'time') {
        effect.push(
          'Best guess: detects whether the local rate of time flow has been altered — sensing artificial time dilation or a "temporally tampered" region. Thinner grounding than the other Measure cases — genuinely speculative.',
        );
      } else if (concept.isEntropy) {
        effect.push(
          'Reads local order/disorder. A self-sustaining, low-entropy region reads as an anomaly against its surroundings — this is "detect life" without Life being a primitive.',
        );
      }
      warnings.push(
        oscillating
          ? "Per Landauer's principle, resetting whatever recorded the measurement costs a small amount of energy — and repeated sampling pays that reset cost every cycle, not once."
          : "Per Landauer's principle, resetting whatever recorded the measurement costs a small amount of energy too — not entirely free.",
      );
      break;
    }

    case 'project': {
      const radial = has(input.modifierIds, 'radial');
      const oscillating = has(input.modifierIds, 'oscillating');
      cost *= radial ? 12.57 : 1;

      if (concept.id === 'space' || concept.id === 'time') {
        // Space/Time aren't stockpiled quantities at a locus the way Mass/Charge/
        // Energy are — they're the framework other quantities are measured within —
        // so "projecting" them as a payload is exotic at best. Speculative, not confident.
        if (concept.id === 'space') {
          effect.push(
            radial
              ? 'Speculative: warps/expands spatial extent around a point — a "bag of holding"-style bubble of enlarged space.'
              : 'Speculative: a directional space-warp corridor — a "long step" shortcut rather than a broad field.',
          );
        } else {
          effect.push(
            radial
              ? "Speculative: a region-wide time-dilation field — the area-effect version of Bind·Time's point-to-point sync."
              : 'Speculative: a corridor of altered time flow rather than a broad field.',
          );
        }
        warnings.push(
          "Genuinely speculative — Space and Time aren't stockpiled quantities at a locus the way Mass/Charge/Energy are, so treat this as an exotic hypothesis, not derived physics.",
        );
        break;
      }

      if (oscillating) cost *= 1.5;

      effect.push(
        radial
          ? `Spreads ${concept.name} outward in all directions from a point.`
          : `Concentrates ${concept.name} along a line — a focused beam rather than a broad field.`,
      );

      if (concept.id === 'charge' && oscillating) {
        effect.push(
          'An oscillating projected Charge is electromagnetic radiation. At the right frequency, that wave is visible light — nothing here says "Light," it falls out of the combination.',
        );
      } else if (concept.id === 'mass' && oscillating) {
        effect.push(
          'By the same logic as Charge: an oscillating projected Mass distribution should radiate gravitational waves, per general relativity. Untested here, but the pattern is the same.',
        );
      } else if (concept.id === 'mass') {
        effect.push('This is what "gravity" resolves to underneath — a projected Mass source.');
      } else if (concept.id === 'energy') {
        effect.push(
          radial
            ? 'A radiant heat source, like a bonfire or the sun.'
            : 'A focused heat-ray — the flamethrower-style effect from the original vision doc.',
        );
      } else if (concept.id === 'momentum') {
        const pulling = has(input.modifierIds, 'reverse');
        if (pulling) {
          effect.push(
            radial
              ? 'An implosion/vacuum field — everything nearby lurches inward toward you instead of away.'
              : 'A tractor beam — pulls whatever it reaches toward you instead of pushing it away.',
          );
          warnings.push(
            'Distinct from Transfer·Momentum·Reverse (which has no reciprocal effect defined here — see Transfer·Momentum above): this still has no second locus to owe a recoil to, so nothing pulls back on you the way shoving one adjacent object would.',
          );
        } else {
          effect.push(radial ? 'An omnidirectional shockwave/knockback.' : 'A directed blast — a battering-ram or wind-gust effect.');
        }
      } else if (concept.id === 'entropy') {
        const decreasing = has(input.modifierIds, 'reverse');
        if (decreasing) {
          cost *= 10;
          effect.push(radial ? 'An area-effect order/preservation field — a ward against decay.' : 'A focused order beam — localized repair or preservation at range.');
          warnings.push(
            "Unlike Transfer·Entropy, Project has no second locus to reciprocate with — the compensating Entropy increase has nowhere to go but the surroundings as waste heat, paid for by the caster. This is where that cost genuinely belongs.",
          );
        } else {
          effect.push(radial ? 'An area-effect decay/corrosion field.' : 'A focused corrosion beam — narrow, not broad.');
        }
        warnings.push(
          "Reopened as an open question: unlike Charge (which the player can Transfer onto themselves first) or Energy (an ambient reserve), there's no Entropy reserve on the caster at all, and Entropy is conserved the same way Transfer's is — Project only ever has one locus to draw from. What an unmarked, self-sourced cast is even supposed to draw on is unresolved; this may end up requiring a marked source rather than ever defaulting to the caster.",
        );
      }
      break;
    }
  }

  if (!concept.dimension && !concept.isEntropy) {
    warnings.push('Unrecognized concept dimension.');
  }

  const costTier: CostTier = unit === 'J' ? 'catastrophic' : tierFromCost(cost);

  return {
    dimensionSummary: formatDimension(concept.dimension),
    costLabel: formatCost(cost, unit),
    costTier,
    effect,
    warnings,
  };
}

export { magnitudeScale };
